<template>
  <q-tab-pane name="contact-person" class="no-padding">
    <q-scroll-area
      class="full-width customer-card__tab-scroll"
      :thumb-style="{ width: '5px','background-color': '#555',opacity: 1 }"
    >
      <template v-if="customer.contact_persons && customer.contact_persons.data && customer.contact_persons.data.length > 0">
        <div class="row sticky-top bg-white q-px-md q-py-sm border-bottom customer-card__toolbar" v-is-user>
          <q-btn :label="$tc('contact_person', 1)" icon="add" color="gray" flat @click="handleCreateContactPerson"/>
        </div>
        <q-table
          row-key="id"
          :columns="tableColumns"
          :data="mappedContactPersons"
          hide-header
          hide-bottom
          binary-state-sort
          class="no-shadow"
        >
          <q-td slot="body-cell-actions" slot-scope="props" :props="props">
            <q-btn icon="more_vert" color="grey-7" flat round dense>
              <q-popover>
                <q-list link class="no-border">
                  <q-item
                    v-close-overlay
                    @click.native="handleUpdate(props.row)"
                  >
                    <q-item-side icon="edit"/>
                    <q-item-main :label="$t('edit')"/>
                  </q-item>
                  <q-item
                    v-close-overlay
                    @click.native="handleDelete(props.row)"
                  >
                    <q-item-side icon="delete"/>
                    <q-item-main :label="$t('delete')"/>
                  </q-item>
                </q-list>
              </q-popover>
            </q-btn>
          </q-td>
        </q-table>
      </template>
      <template v-else>
        <div class="text-center justify-center items-center col-grow q-pt-lg">
          <img src="~assets/emptystates/contacts_empty.svg" height="100px"/>

          <h6 class="q-my-md text-faded">{{ $t('no_contact_person') }}</h6>

          <q-btn @click="handleCreateContactPerson" color="primary" class="q-mt-lg" outline>
            {{ $t('create_contact_person') }}
          </q-btn>
        </div>
      </template>
    </q-scroll-area>
    <default-modal
      v-model="modalOpened"
      name="add-customer-modal"
      :autofocus="$refs.addCustomerForm"
      :successHandler="handleSaveCustomer"
      :title="$t('create_contact_person')"
      width="sm"
    >
      <add-contact-person-form ref="addCustomerForm" slot="content" v-model="formData" :action="action"/>
    </default-modal>
  </q-tab-pane>
</template>

<script>
  import AddContactPersonForm from 'components/Customers/Companies/AddContactPersonForm'
  import DefaultModal from 'components/Modal/DefaultModal'
  import EmailSubResource from 'src/utils/SubResources/CustomerEmailAddress'
  import PhoneSubResource from 'src/utils/SubResources/CustomerPhoneNumber'
  import SubResourceHelper from 'src/utils/SubResourceHelper'
  import changeCase from 'change-case-object'
  import cleanDeep from 'clean-deep'
  import { createErrorFromResponse } from 'src/utils/error'
  import { mapActions } from 'vuex'

  function initialState () {
    return {
      customer_number: '',
      salutation: 'ms',
      firstName: null,
      lastName: '',
      emailAddresses: [
        EmailSubResource.empty()
      ],
      phoneNumbers: [
        PhoneSubResource.empty()
      ]
    }
  }

  export default {
    components: { AddContactPersonForm, DefaultModal },
    data () {
      return {
        modalOpened: false,
        formData: initialState(),
        original: null,
        action: 'create'
      }
    },
    props: {
      customer: {
        type: Object,
        required: true
      }
    },
    computed: {
      saveMethod () {
        return this.action === 'create'
          ? this.createContactPerson
          : this.updateContactPerson
      },
      mappedContactPersons () {
        if (!this.customer || !this.customer.contact_persons.data) {
          return []
        }

        return this.customer.contact_persons.data.map(contact => {
          return {
            name: [contact.title, contact.first_name, contact.last_name].join(' '),
            email: contact.email_addresses
              ? contact.email_addresses.data[0].email
              : null,
            phone: contact.phone_numbers
              ? contact.phone_numbers.data[0].number
              : null,
            ...contact
          }
        })
      },
      tableColumns () {
        return [{
          name: 'name',
          field: 'name',
          align: 'left'
        }, {
          name: 'email',
          field: 'email',
          align: 'left'
        }, {
          name: 'phone',
          field: 'phone',
          align: 'left'
        }, {
          name: 'actions',
          align: 'right'
        }]
      }
    },
    methods: {
      ...mapActions(['createContactPerson', 'updateContactPerson', 'removeContactPerson']),

      handleCreateContactPerson () {
        this.reset()
        this.action = 'create'
        this.modalOpened = true
      },
      handleUpdate (person) {
        person = changeCase.camel(person)
        person = EmailSubResource.parseResponseData(person)
        person = PhoneSubResource.parseResponseData(person)

        this.original = JSON.parse(JSON.stringify(person))

        this.$refs.addCustomerForm.loadData(person)
        this.action = 'update'
        this.modalOpened = true
      },
      handleDelete (person) {
        this.$q.dialog({
          title: this.$t('delete_contact_person_title'),
          message: this.$t('delete_contact_person_text'),
          ok: this.$t('confirm'),
          cancel: this.$t('cancel')
        }).then(() => {
          this.removeContactPerson({
            customerId: person.company_id,
            personId: person.id
          })
        }).catch(() => {})
      },
      handleSaveCustomer () {
        let promise
        let subResourcesFailed = false

        if (this.action === 'update') {
          promise = this.storeSubResources()
            .catch(error => {
              subResourcesFailed = true

              createErrorFromResponse(error)
            })
        } else {
          promise = Promise.resolve()
        }

        promise
          .then(() => {
            return this.saveMethod({
              person: this.getData(),
              customerId: this.customer.id
            })
          })
          .then(() => {
            if (!subResourcesFailed) {
              this.reset()
            }
          })
          .catch(createErrorFromResponse)
      },
      getData () {
        const data = cleanDeep(changeCase.snakeCase(this.formData))
        delete data['default_phone_number_id']
        delete data['default_email_address_id']

        return data
      },
      storeSubResources () {
        const emails = new SubResourceHelper(this.original.emailAddresses, new EmailSubResource(this.original.id, 'person', this.customer.id))
        const phones = new SubResourceHelper(this.original.phoneNumbers, new PhoneSubResource(this.original.id, 'person', this.customer.id))

        return Promise.all([
          emails.diff(this.formData.emailAddresses),
          phones.diff(this.formData.phoneNumbers)
        ])
      },
      reset () {
        this.original = null
        this.modalOpened = false
        this.$refs.addCustomerForm.resetData()
      }
    }
  }
</script>
